/**
 * @file SiteSettingsPanel.tsx
 * @description Панель «Настройки сайта» (phase19.16 Р1; SB18, SB19, SB59,
 * SB71, SB86; SC56; uiux-design-r1 §4): разделы «Бренд», «Контакты»,
 * «Соцсети», «Подвал», «Продавец» (только показ), «Доступ к сайту»
 * (пауза «Сайт закрыт на работы», SB138 — `SiteAccessSection`). С 1280px — две колонки
 * (разделы слева 264px, раздел справа на всю оставшуюся ширину, §7.16), ниже — список разделов отдельным экраном
 * и раздел со стрелкой «назад».
 *
 * Самостоятельная панель без маршрута: выбранный раздел — снаружи
 * (`section` / `onSection`; страница хранит его в адресе, оболочка
 * «Сайт → Конструктор» — у себя), бренд — общая часть каркаса.
 */

import { ChevronLeft } from "lucide-react";

import { SiteAccessSection } from "../../site-access/SiteAccessSection";
import { SaveIndicator } from "../SaveIndicator";
import { SellerLegalView } from "../SellerLegalBlock";
import { UnsavedGuard } from "../UnsavedGuard";
import { BrandSection } from "./BrandSection";
import { ContactsSection, SocialsSection } from "./ContactsSections";
import { FooterTextSection } from "./FooterTextSection";
import { SettingsSectionNav, type SettingsSection } from "./SettingsSectionNav";
import { useContactRows } from "../../../hooks/site-view/useContactRows";
import { useSellerInfo } from "../../../hooks/site-view/useSellerInfo";
import { useSiteDraftPart } from "../../../hooks/site-view/useSiteDraftParts";
import { WIDE_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import {
  SITE_SETTINGS_SECTION_LABELS,
  SITE_SETTINGS_SECTIONS,
  type SiteSettingsSectionKey,
} from "../../../lib/site-view/settings-sections";

const LABELS = SITE_SETTINGS_SECTION_LABELS;
const KEYS = SITE_SETTINGS_SECTIONS;

interface Props {
  /** Открытый раздел; `null` — на узком экране список разделов, на широком — «Бренд». */
  section: SiteSettingsSectionKey | null;
  onSection: (section: SiteSettingsSectionKey | null) => void;
  /** Узкая колонка: список разделов, раздел — со стрелкой «назад» (признак от места, не от окна). */
  narrow?: boolean;
}

export function SiteSettingsPanel({ section, onSection, narrow = false }: Props) {
  const wideWindow = useMediaQuery(WIDE_QUERY);
  const wide = wideWindow && !narrow;
  const brand = useSiteDraftPart("brand");
  const contacts = useContactRows(brand);
  const seller = useSellerInfo(brand.fields.shopContactEmail);
  const current = section ?? (wide ? "brand" : null);
  const sellerError = !!seller.info && seller.info.missing.length > 0;
  const sections: SettingsSection<SiteSettingsSectionKey>[] = KEYS.map((key) => ({ key, label: LABELS[key], error: key === "seller" && sellerError }));

  const body = current ? (
    <section aria-label={LABELS[current]} className="flex min-w-0 flex-col gap-4 rounded-xl bg-surface-lowest p-4 [box-shadow:inset_0_0_0_1px_var(--border)] sm:p-6" data-settings-section="">
      <h2 className="type-section-title text-on-surface">{LABELS[current]}</h2>
      {/* «Доступ к сайту» не правит черновик бренда — его индикатор сохранения там лишний. */}
      {current !== "access" ? <SaveIndicator state={brand} /> : null}
      {current === "brand" ? <BrandSection brand={brand} /> : null}
      {current === "contacts" ? <ContactsSection brand={brand} contacts={contacts} /> : null}
      {current === "socials" ? <SocialsSection brand={brand} contacts={contacts} /> : null}
      {current === "footer" ? <FooterTextSection brand={brand} /> : null}
      {current === "seller" ? <SellerLegalView shopEmail={brand.fields.shopContactEmail} seller={seller} showSource /> : null}
      {current === "access" ? <SiteAccessSection /> : null}
    </section>
  ) : null;
  const guard = <UnsavedGuard when={brand.status === "error" || brand.status === "conflict"} />;

  if (wide) {
    return (
      <div className="grid grid-cols-[16.5rem_minmax(0,1fr)] items-start gap-8">
        <SettingsSectionNav sections={sections} current={current} onSelect={onSection} variant="column" />
        {body}
        {guard}
      </div>
    );
  }
  if (!current) return <SettingsSectionNav sections={sections} current={null} onSelect={onSection} variant="screen" />;
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => onSection(null)}
        className="-ml-2 inline-flex min-h-12 items-center gap-1 self-start rounded-md px-2 text-sm font-medium text-primary outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronLeft aria-hidden className="size-5" />
        Разделы
      </button>
      {body}
      {guard}
    </div>
  );
}
